<template>
  <div class="collaborator-list-wrp">
    <div class="flex text-xs" v-if="!hideCollaboratorsList && !loadingCollaborators">
      <div class="flex-grow">
        {{ $i18nPresenter.t('Editors') }}
        <aha-icon
          name="question-o"
          height="12"
          width="12"
          viewBox="0 0 12 12"
          maintain-color
          v-tooltip="$i18nPresenter.t(
            'Can edit content, present, and access all presenter reports.'
          )"
        />
      </div>
    </div>

    <div v-if="loadingCollaborators" class="flex justify-center">
      <aha-spinner class="py-5" />
    </div>
    <ul
      v-else-if="!hideCollaboratorsList"
      class="collaborator-list share-v2"
      @scroll="onScrollCollaborateList"
    >
      <li
        v-for="(collaborator, index) in collaborators"
        :key="collaborator.email"
        class="collaborator-item individual-collaborator-item"
        :class="{'hidden': collaborator.email === ahaSystem.email}"
      >
        <div class="flex flex-row items-center">
          <aha-avatar
            class="mr-2"
            :name="getCollaboratorName(collaborator)"
            :image="collaborator.avatar"
            :width="36"
            :height="36"
            :id="collaborator.id"
          />
          <div class="collaborator-info">
            <div class="collaborator-info__name mb-1">
              <span class="relative">
                {{ getCollaboratorName(collaborator) }}
              </span>
            </div>
            <p class="collaborator-info__email">
              {{ collaborator.email }}
            </p>
          </div>
          <div class="collaborator-role" :id="`collaborator-${collaborator.email}`">
            <span v-if="collaborator.role === 'owner'">
              {{ $i18nPresenter.t('Owner') }}
            </span>
            <span v-else-if="!canViewerShare && collaborator.id !== currentUser.id" class="capitalize">
              {{ collaborator.role }}
            </span>
            <span
              v-else
              @click="toggleCollaboratorDropdown(collaborator.email)"
              class="collaborator-role--dropdown-arrow"
            >
              {{ getRoleName(collaborator.role) }}

              <aha-icon
                name="chevron-down-new"
                height="12"
                width="12"
                viewBox="0 0 12 12"
              />
            </span>
            <aha-context-menu
              :classList="'force-z-index'"
              :ref="`collaborator-menu-${collaborator.email}`"
              :target="`collaborator-${collaborator.email}`"
              :forceMenuPosition="true"
              :x="0"
              :y="getDropdownY(index)"
              @hide="onHideDropdown"
            >
              <aha-context-menu-item
                class="text-left"
                @click="changeRole(roles.EDITOR, collaborator)"
                v-if="isShowUpdateEditor(collaborator)"
              >
                <aha-icon
                  name="check"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  class="mr-1 text-primary-90 mt-3"
                  :style="{visibility: collaborator.role === roles.EDITOR ? '' : 'hidden'}"
                />
                <div class="grid w-48 py-2">
                  <span class="leading-6 font-bold text-sm capitalize">{{ roles.EDITOR }}</span>
                  <span class="text-xs whitespace-normal">
                    {{ $i18nPresenter.t('Edit and present') }}
                  </span>
                </div>
              </aha-context-menu-item>
              <aha-context-menu-item
                class="text-left"
                @click="changeRole(roles.VIEWER, collaborator, $event)"
                v-if="isShowUpdateViewer(collaborator)"
              >
                <aha-icon
                  name="check"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  class="mr-1 text-primary-90 mt-3"
                  :style="{visibility: collaborator.role === roles.VIEWER ? '' : 'hidden'}"
                />
                <div class="grid w-48 py-2">
                  <span class="leading-6 font-bold text-sm capitalize">
                    {{ roles.VIEWER }}
                    <IconNeedUpgradePlan
                      v-if="planBenefit.teamWorkCollaboration &&
                        !planBenefit.teamWorkCollaboration.presentOnlyRole"
                      ref="iconNeedUpgradePlanComponent"
                      :noPermission="planBenefit.teamWorkCollaboration &&
                        !planBenefit.teamWorkCollaboration.presentOnlyRole"
                      :upgradePlan="planBenefit.teamWorkCollaboration.presentOnlyRole_planName"
                      featureName="Collaboration View Role"
                    />
                  </span>
                  <span class="text-xs whitespace-normal">{{ $i18nPresenter.t('View and present') }} </span>
                </div>
              </aha-context-menu-item>
              <aha-context-menu-item
                class="text-left border-t border-solid border-base-30"
                :disabled="canNotRemoveCollaborator(collaborator)"
                @click="removeCollaborator(collaborator)"
                v-if="hasPermissionRemoveMember(collaborator)"
              >
                <aha-icon
                  name="check"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  class="mr-1 text-primary-90"
                  style="visibility: hidden"
                />
                <div class="grid w-48 py-2">
                  <span class="leading-6 text-sm">{{ $i18nPresenter.t('Remove') }}</span>
                </div>
              </aha-context-menu-item>
              <aha-context-menu-item
                class="text-left border-t border-solid border-base-30"
                :disabled="canNotRemoveCollaborator(collaborator)"
                @click="leaveCollaborator(collaborator)"
                v-if="hasPermissionLeave(collaborator)"
              >
                <aha-icon
                  name="check"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  class="mr-1 text-primary-90"
                  style="visibility: hidden"
                />
                <div class="grid w-48 py-2">
                  <span class="leading-6 text-sm">{{ $i18nPresenter.t('Leave') }}</span>
                </div>
              </aha-context-menu-item>
            </aha-context-menu>
          </div>
        </div>
      </li>
      <div v-if="teamCollaborators.length > 0" class="border-b border-solid border-base-30" @click.stop />
      <div class="grid gap-4">
        <div class="grid gap-3" v-for="(teamCollaborator, index) in teamCollaborators" :key="index">
          <div class="py-3 flex justify-between items-center">
            <div>
              <div class="font-bold text-base">
                {{ teamCollaborator.accountName }}
              </div>
              <div class="text-xs text-base-80">
                {{ teamCollaborator.users.length > 1 || teamCollaborator.users.length === 0 ?
                  `${teamCollaborator.users.length} ${$i18nPresenter.t('members')}` :
                  `${teamCollaborator.users.length} ${$i18nPresenter.t('member')}` }}
              </div>
            </div>
            <div>
              <div
                v-if="!hasPermissionRemoveTeam(teamCollaborator)"
                class="collaborator-role capitalize"
                :id="`collaborator-${teamCollaborator.accountId}`"
              >
                {{ teamCollaborator.accountRole }}
              </div>
              <span
                v-else
                @click="toggleAccountCollaboratorDropdown(teamCollaborator.accountId)"
                class="collaborator-role--dropdown-arrow"
              >
                {{ teamCollaborator.accountRole }}
                <aha-icon
                  name="arrow"
                  height="12"
                  width="12"
                  viewBox="0 0 24 24"
                />
              </span>
              <aha-context-menu
                :classList="'force-z-index'"
                :ref="`collaborator-menu-account-${teamCollaborator.accountId}`"
                :target="`collaborator-${teamCollaborator.accountId}`"
                :forceMenuPosition="true"
                :x="0"
                :y="getDropdownY(index)"
                @hide="onHideDropdown"
              >
                <aha-context-menu-item
                  @click="removeCollaborator(teamCollaborator)"
                >
                  <span style="margin-left: 0">
                    {{ $i18nPresenter.t('Remove') }}
                  </span>
                </aha-context-menu-item>
              </aha-context-menu>
            </div>
          </div>
          <div class="team-user" v-for="(user, key) in teamCollaborator.users" :key="key">
            <div class="flex flex-row items-center">
              <aha-avatar
                class="mr-2"
                :name="getCollaboratorName(user)"
                :image="user.avatar"
                :width="36"
                :height="36"
                :id="user.id"
              />
              <div class="collaborator-info">
                <div class="collaborator-info__name mb-1">
                  <span class="relative">
                    {{ getCollaboratorName(user) }}
                  </span>
                </div>
                <p class="collaborator-info__email">
                  {{ user.email }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PresentationInstructorList
        class="mt-2 border-t pt-3 present-instructor"
        v-if="canUseShareDocs && presentation && presentation.id"
        :presentationId="presentation.id"
      />
    </ul>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import constants from '@/constant'
import EmailUtils from '@/utils/email'
import IconNeedUpgradePlan from '@/components-v2/my-plan/IconNeedUpgradePlan.vue'
import PresentationInstructorList from '@/components-v2/presentation-editor/collaboration/PresentationInstructorList.vue'

export default {
  components: {
    IconNeedUpgradePlan,
    PresentationInstructorList,
  },
  props: {
    folder: {
      type: Object,
      default: () => {},
    },
    presentation: {
      type: Object,
      default: () => {},
    },
    loadingCollaborators: {
      type: Boolean,
      default: false,
    },
    collaborators: {
      type: Array,
      default: () => [],
    },
    teamCollaborators: {
      type: Array,
      default: () => [],
    },
    isDisableEditPresentation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      targetX: null,
      targetY: null,
      scrollTop: 0,
      permissions: constants.permissions,
      roles: constants.role,
      ahaSystem: constants.ahaSystem,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      canEditPresentation: (state) => state.canEditPresentation,
      canViewerShare: (state) => state.canViewerShare,
      planBenefit: (state) => state.planBenefit,
    }),
    ...mapGetters('multipleSessions', ['canUseShareDocs']),

    currentOwner() {
      if (this.presentation) {
        return this.presentation.User
      }
      if (this.folder) {
        return this.folder.User
      }
      return { User: {} }
    },

    hideCollaboratorsList() {
      // Currently hide the collaborators list when the current user is an instructor
      // Why: the instructors list is not available for instructor at the moment. Will be implemented in the future
      return this.canUseShareDocs && this.collaborators.some((c) => {
        return c.role === constants.role.INSTRUCTOR &&
         c.presentationId === this.presentation.id &&
         c.id === this.currentUser.id
      })
    },
  },
  methods: {
    onScrollCollaborateList: _.throttle(function checkScrollTop(e) {
      this.scrollTop = e.srcElement.scrollTop
    }, 500),
    getDropdownY(index) {
      const listHeight = this.collaborators.length >= 4 ? 240 : this.collaborators.length * 60
      const dropdownHeight = 80
      const spaceBetweenItems = 70
      return (listHeight - index * spaceBetweenItems + this.scrollTop < dropdownHeight) ? -90 : 5
    },
    getCollaboratorName(collaborator) {
      if (collaborator.id && collaborator.firstName) {
        return `${collaborator.firstName} ${collaborator.lastName}`
      }
      if (collaborator.email) {
        return EmailUtils.getEmailName(collaborator.email)
      }
      return 'Unknown user'
    },
    toggleCollaboratorDropdown(email) {
      // Fix AHA-5280: The modal content height is automatically increasing on Firefox
      // So we need to fix a height here
      const elem = document.querySelector('.aha-modal__content')
      if (elem) {
        elem.style.height = `${elem.scrollHeight}px`
      }
      // Open the dropdown menu
      this.$refs[`collaborator-menu-${email}`][0].show()
    },
    toggleAccountCollaboratorDropdown(accountId) {
      const elem = document.querySelector('.aha-modal__content')
      if (elem) {
        elem.style.height = `${elem.scrollHeight}px`
      }
      // Open the dropdown menu
      this.$refs[`collaborator-menu-account-${accountId}`][0].show()
    },

    onHideDropdown() {
      const elem = document.querySelector('.aha-modal__content')
      if (elem) {
        elem.style.height = null
      }
    },

    canNotRemoveCollaborator(collaborator) {
      // Can remove non-user
      if (!collaborator.id) return false
      // Can remove myself (leave collaborating)
      if (collaborator.id === this.currentUser.id) return false
      // Cannot remove current editor, but can remove the others
      if (this.presentation) return (this.presentation.takenOverBy === collaborator.id)
      if (this.folder) return (this.folder.takenOverBy === collaborator.id)
      return false
    },

    removeCollaborator(collaborator) {
      this.$emit('handleRemoveCollaborator', collaborator, 'remove')
    },
    leaveCollaborator(collaborator) {
      this.$emit('handleRemoveCollaborator', collaborator, 'leave')
    },
    hasPermissionRemoveMember(collaborator) {
      if (this.folder) {
        if (this.folder.folderId !== collaborator.folderId || this.currentUser.id === collaborator.id) {
          return false
        }
        return this.currentUser.id === this.folder.ownerId ||
        (this.folder.permissions && this.folder.permissions.includes(this.permissions.INVITE_TO_EDIT))
      }
      if (this.presentation) {
        if (this.presentation.id !== collaborator.presentationId || this.currentUser.id === collaborator.id) {
          return false
        }
        // return this.currentUser.id === this.presentation.userId ||
        return (this.presentation.permissions &&
          this.presentation.permissions.includes(this.permissions.INVITE_TO_EDIT))
      }
      return false
    },
    hasPermissionRemoveTeam(teamCollaborator) {
      if (this.folder) {
        return this.folder.folderId === teamCollaborator.folderId && this.currentUser.id === this.folder.ownerId
      }
      if (this.presentation) {
        return this.presentation.id === teamCollaborator.presentationId
      }
      return false
    },
    hasPermissionLeave(collaborator) {
      if (this.folder) {
        if (this.folder.folderId !== collaborator.folderId) {
          return false
        }
        return collaborator.id ===
          this.currentUser.id
      }
      if (this.presentation) {
        return collaborator.id === this.currentUser.id &&
          collaborator.presentationId === this.presentation.id
      }
      return false
    },
    changeRole(role, collaborator, event) {
      if (this.planBenefit.teamWorkCollaboration &&
        !this.planBenefit.teamWorkCollaboration.presentOnlyRole && event && role === this.roles.VIEWER) {
        if (this.$refs.iconNeedUpgradePlanComponent && this.$refs.iconNeedUpgradePlanComponent.length) {
          event.stopPropagation()
          this.$refs.iconNeedUpgradePlanComponent[0].toggleTooltip()
          return
        }
      }
      if (role !== collaborator.role) {
        this.$emit('handleChangeCollaboratorRole', collaborator, role)
      }
    },
    isShowUpdateEditor(collaborator) {
      if (collaborator.id !== this.currentUser.id) {
        return true
      }
      if (this.folder) {
        return this.folder.permissions && this.folder.permissions.includes(this.permissions.INVITE_TO_EDIT)
      }
      return this.presentation.permissions && this.presentation.permissions.includes(this.permissions.INVITE_TO_EDIT)
    },
    isShowUpdateViewer(collaborator) {
      if (this.canUseShareDocs) {
        return false
      }
      if (this.folder && this.currentUser.id === collaborator.id &&
        this.folder.permissions && this.folder.permissions.includes(this.permissions.INVITE_TO_EDIT)) {
        return false
      }
      if (this.presentation && this.currentUser.id === collaborator.id &&
      // if (this.presentation &&
        this.presentation.permissions && this.presentation.permissions.includes(this.permissions.INVITE_TO_EDIT)) {
        return false
      }
      return true
    },
    getRoleName(roleName) {
      if (roleName === constants.role.INSTRUCTOR) {
        return 'Presenter'
      }
      return roleName
    },
  },
}
</script>

<style lang="scss" scoped>
.collaborator-list-wrp {
  ::v-deep .context-menu-wrapper {
    min-width: 6rem;
    padding: 4px;
  }
  ::v-deep .context-menu-item {
    align-items: flex-start;
  }
  @media (max-width: 767px) {
    height: 100%;
  }
}

.collaborator-list {
  @apply pr-2 max-h-60 overflow-y-auto;
  &.share-v2{
    height: 370px;
    max-height: none;

    @media (max-width: 767px) {
      height: calc(100% - 110px)
    }
  }
}

.collaborator-item {
  @apply py-3;
}

.collaborator-info {
  width: 70%;
}

.collaborator-info__name {
  @apply font-bold;
}
.collaborator-info__email {
  @apply text-base-80 text-xs;
}

.collaborator-role {
  @apply ml-auto text-base-90 text-xs text-right relative;
  min-width: 60px;
}
.collaborator-role--dropdown {
  @apply bg-base-0 absolute top-5 right-0;
  @apply border border-base-30;
  @apply rounded w-24 z-30;
  box-shadow: 3px 3px 5px rgba(211, 215, 225, 0.42);
}
.collaborator-role--dropdown-item {
  @apply p-2;
  &.clickable {
    &:hover {
      @apply text-red-100;
    }
    @apply cursor-pointer;
  }
}
.collaborator-role--dropdown-arrow {
  @apply cursor-pointer z-20 capitalize;
}
.icon-empty {
  width: 16px;
}
.team-user {
  .collaborator-role {
    color: rgba(49, 49, 49, 0.5);
  }
  .collaborator-role--dropdown-arrow {
  @apply cursor-not-allowed z-20 capitalize;
}
}

.present-instructor {
  border-color: #ebebeb;
}

</style>
